import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  CollectionNames,
  ProjectServiceCreateResponseJson,
  ProjectServiceCreateResponseSchema,
  ProjectServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, filter, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { ProjectRepositoryService, withSearchSync } from '../../repositories';
import { VOID } from '../../shared/utils';
import * as ProjectsActions from './projects.actions';
import { ProjectMinimal } from './projects.reducer';

@Injectable()
export class ProjectsEffects extends AuthRunEffects {
  private readonly repository = inject(ProjectRepositoryService);
  private readonly injector = inject(EnvironmentInjector);
  private readonly errorHandler = inject(GlobalErrorHandlerService);

  listProjects$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.listProjects),
      concatLatestFrom(() => of(true)),
      filter(([, loading]) => loading), // see https://github.com/eslint/eslint/issues/7838#issuecomment-269982977
      withSearchSync(CollectionNames.Projects, this.injector),
      switchMap(([payload]) =>
        this.repository
          .list(fromJson(ProjectServiceListRequestSchema, payload.params))
          .pipe(
            map((result) =>
              ProjectsActions.loadProjects({
                projects: (result.items ?? []).map(
                  (item) =>
                    toJson(
                      ProjectServiceCreateResponseSchema,
                      item,
                      defaultApiSerializeJsonOptions,
                    ) as ProjectServiceCreateResponseJson,
                ) as ProjectMinimal[],
              }),
            ),
            catchError((error) => {
              this.errorHandler.handleError(error);
              return of(ProjectsActions.listProjectsFailure(error));
            }),
          ),
      ),
    );
  });

  loadProjects$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.loadProjects),
      /** An EMPTY observable only emits completion. Replace with your own observable API request */
      concatMap(() => EMPTY as Observable<{ type: string }>),
    );
  });

  deleteProject$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ProjectsActions.deleteProject),
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        switchMap(({ id }) => this.repository.delete(id)),
        catchError((error) => {
          this.errorHandler.handleError(error);
          return VOID;
        }),
      );
    },
    { dispatch: false },
  );

  constructor() {
    super(inject(Actions));
  }
}
